import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { getMessagesContentBootstrap } from "@/app/actions/messages-content/bootstrap/action";
import { buildMessageSequenceQueryKey } from "@/app/hooks/fetchs/messages-content/useFetchMessageSequence";
import { buildSequenceSummaryQueryKey } from "@/app/hooks/fetchs/messages-content/useFetchMessageSequenceSummary";
import { buildSequenceObjectivesQueryKey } from "@/app/hooks/fetchs/messages-content/useFetchSequenceObjectives";
import { MessagesContentBootstrap } from "@/app/types/messages-content/MessagesContentBootstrap";
import { UCookie } from "@/app/utils/UCookie";
import { UMessageChannel } from "@/app/utils/UMessageChannel";
import { QueryKey } from "@/app/vars/QueryKeys";
import { CookieKey } from "@/app/vars/Storage";

type Options = {
  projectId?: number;
  enabled?: boolean;
  initialData?: MessagesContentBootstrap;
};

export function useFetchMessagesContentBootstrap(options: Options) {
  const { projectId, enabled = true, initialData } = options;
  const queryClient = useQueryClient();

  const seedCaches = (bootstrap: MessagesContentBootstrap) => {
    queryClient.setQueryData([QueryKey.PROJECT_OFFER, projectId], bootstrap.offers);

    if (bootstrap.selectedOfferId) {
      queryClient.setQueryData(
        [QueryKey.CLIENT_AVATAR, bootstrap.selectedOfferId],
        bootstrap.avatars,
      );
    }

    if (bootstrap.selectedAvatarId) {
      queryClient.setQueryData(
        buildSequenceObjectivesQueryKey(bootstrap.selectedAvatarId),
        bootstrap.objectives,
      );
    }

    if (bootstrap.selectedObjectiveId && bootstrap.workspace) {
      queryClient.setQueryData(
        buildSequenceSummaryQueryKey(bootstrap.selectedObjectiveId, bootstrap.channel),
        bootstrap.workspace.summary,
      );
      queryClient.setQueryData(
        buildMessageSequenceQueryKey(bootstrap.selectedObjectiveId, bootstrap.channel),
        bootstrap.workspace.sequence,
      );
    }
  };

  const seededProjectId = useRef<number | undefined>(undefined);
  if (initialData && projectId && seededProjectId.current !== projectId) {
    seedCaches(initialData);
    seededProjectId.current = projectId;
  }

  return useQuery({
    queryKey: [QueryKey.MESSAGES_CONTENT_BOOTSTRAP, projectId],
    queryFn: async () => {
      const hintOfferId = UCookie.readNumber(CookieKey.SELECTED_OFFER);
      const hintAvatarId = UCookie.readNumber(CookieKey.SELECTED_AVATAR);
      const hintObjectiveId = UCookie.readNumber(CookieKey.MESSAGES_CONTENT_SELECTED_OBJECTIVE);

      const bootstrap = await getMessagesContentBootstrap(
        projectId!,
        hintOfferId,
        hintAvatarId,
        hintObjectiveId,
        UMessageChannel.readStored(),
      );
      seedCaches(bootstrap);

      return bootstrap;
    },
    enabled: enabled && !!projectId,
    staleTime: Infinity,
    initialData: seededProjectId.current === projectId ? initialData : undefined,
  });
}
